import { useEffect, useState, useRef } from 'react';
import { getCollectionsByNetwork } from '@/lib/collections';
import { fetchSandboxCollectionTokens } from '@/lib/tzkt';
import { fetchFilteredTokens, fetchCollectionTraits } from '@/lib/api';
import { getCardImageUrl } from '@/lib/ipfs';
import { useAppNetwork } from '@/hooks/useAppNetwork';
import { CollectionCard, CollectionWithStats } from '@/components/ui/CollectionCard';

export default function HomePage() {
  const [collections, setCollections] = useState<CollectionWithStats[]>([]);
  const { network: appNetwork } = useAppNetwork();
  const bannerRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  // Parallax scroll effect for banner
  useEffect(() => {
    const handleScroll = () => {
      if (!bannerRef.current) return;

      // Calculate how far user has scrolled relative to viewport height
      // Use a larger scroll range for smoother effect
      const scrollRange = window.innerHeight;
      const scrollY = window.scrollY;
      const progress = Math.min(scrollY / scrollRange, 1);
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const isSandbox = appNetwork === 'sandbox';

    const networkCollections = getCollectionsByNetwork(appNetwork);

    setCollections(
      networkCollections.map(c => ({ ...c, isLoading: true }))
    );

    networkCollections.forEach(async (collection) => {
      try {
        let stats;
        let sampleImage: string | undefined;

        if (isSandbox) {
          const result = await fetchSandboxCollectionTokens(collection.address, { offset: 0, limit: 1 });
          if (result.items.length > 0 && result.items[0].metadata?.image) {
            sampleImage = getCardImageUrl(result.items[0].metadata.image);
          }
          stats = {
            address: collection.address,
            tokenCount: result.pagination.totalCount || result.items.length,
            holderCount: 1,
            lastUpdated: new Date(),
          };
        } else {
          // Use our API server for mainnet collections (has cached data)
          const traitsPromise = fetchCollectionTraits(collection.address)
            .then(data => ({
              address: collection.address,
              tokenCount: data.totalSupply,
              holderCount: 0, // Not available from traits endpoint
              lastUpdated: new Date(),
            }))
            .catch(() => undefined);
          const samplePromise = fetchFilteredTokens(collection.address, { limit: 1 })
            .then(result => {
              if (result.items.length > 0 && result.items[0].metadata?.image) {
                return getCardImageUrl(result.items[0].metadata.image);
              }
              return undefined;
            })
            .catch(() => undefined);

          [stats, sampleImage] = await Promise.all([traitsPromise, samplePromise]);
        }

        setCollections(prev =>
          prev.map(c =>
            c.slug === collection.slug
              ? { ...c, stats, sampleImage, isLoading: false }
              : c
          )
        );
      } catch (err) {
        console.error('Failed to fetch collection data:', collection.slug, err);
        setCollections(prev =>
          prev.map(c =>
            c.slug === collection.slug
              ? { ...c, isLoading: false }
              : c
          )
        );
      }
    });
  }, [appNetwork]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Banner with Parallax and Overlaid Title */}
      <div ref={bannerRef} className="w-full h-80 overflow-hidden relative">
        <img
          src="/tezos-marketplace-banner.png"
          alt="Tezos Marketplace"
          className="absolute w-full"
          style={{
            // Scale image larger so there's room to move it
            height: '150%',
            objectFit: 'cover',
            // Move from showing top (0%) to bottom (-50% which is the extra 50%)
            top: `${-scrollProgress * 50}%`,
          }}
        />
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-black/40" />
        {/* Hero Text Overlay */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 drop-shadow-lg">
            Tezos Marketplace
          </h1>
          <p className="text-lg md:text-xl text-gray-100 max-w-2xl mx-auto drop-shadow-md">
            A uniquely curated experience showcasing the finest collections.
          </p>
        </div>
      </div>

      {/* Featured Collections */}
      <div className="container mx-auto px-4 py-12">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Featured Collections</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {collections.map((collection) => (
            <CollectionCard key={collection.slug} collection={collection} />
          ))}
        </div>

        {collections.length === 0 && (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            Loading collections...
          </div>
        )}
      </div>
    </div>
  );
}
